import { useState } from 'react';
import { isZeroBudgetField } from '../utils/budgetFieldValue';

// Clearing a zero field on focus is display-only: nothing is written, so an
// autosave never fires for an edit that never happened, and the next keystroke is
// the whole value instead of landing in front of a 0. Writing '' instead does not
// work — Forecast and Actuals display the Offer value while their own field is
// blank, and TicketsSection coerces '' straight back to 0 (numericFields), so in
// both cases the field would still read "0.0".
//
// Two rules the call sites have to honour together, which is why this is one hook
// rather than a helper per handler:
//
//   value:   isCleared(item, key) ? '' : <whatever the column displays>
//   onFocus: focus(item, key, <that same displayed value>)
//
// An onFocus without the guarded value neither clears nor selects, and typing 5
// into a 0 yields 50 — that shipped on two Tickets columns. (O-4781)
export default function useZeroClearOnFocus() {
  const [clearedField, setClearedField] = useState(null);

  const fieldKey = (item, key) => `${item.uuid || item.id}:${key}`;

  const isCleared = (item, key) => clearedField === fieldKey(item, key);

  const focus = (item, key, displayedValue, enabled = true) => (e) => {
    if (!enabled || !isZeroBudgetField(displayedValue)) {
      e.target.select();
      return;
    }
    setClearedField(fieldKey(item, key));
  };

  // The clear ends at the first keystroke, not at blur: held any longer, the
  // controlled input re-renders as '' after every change and each onChange
  // carries only the character just typed.
  const release = (item, key) =>
    setClearedField((current) => (current === fieldKey(item, key) ? null : current));

  return { isCleared, focus, release };
}
